import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CreditCard, Store, Loader2, PartyPopper } from "lucide-react";
import { createCheckout, confirmBooking } from "@/lib/api";
import { toast } from "sonner";

export const BookingDialog = ({ open, onClose, itinerary, sessionId }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Reset the success screen when reopening for a new/updated itinerary.
  const itinId = itinerary && itinerary.id;
  useEffect(() => {
    if (open) { setConfirmed(null); setLoading(false); }
  }, [open, itinId]);

  if (!itinerary) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && form.email.trim();

  const guestPayload = {
    guest_name: form.name.trim(),
    guest_email: form.email.trim(),
    guest_phone: form.phone.trim(),
  };

  const payByLink = async () => {
    if (!valid) return toast.error("Please share your name and email first.");
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await createCheckout({ itinerary_id: itinerary.id, origin_url: origin, ...guestPayload });
      localStorage.setItem("sadara_pending", JSON.stringify({
        itinerary_id: itinerary.id, session_id: res.session_id, ...guestPayload,
      }));
      window.location.href = res.checkout_url;
    } catch (e) {
      setLoading(false);
      toast.error("Could not start payment. Please try again.");
    }
  };

  const payByPos = async () => {
    if (!valid) return toast.error("Please share your name and email first.");
    setLoading(true);
    try {
      const res = await confirmBooking({
        itinerary_id: itinerary.id, payment_method: "POS (In person)", ...guestPayload,
      });
      setConfirmed(res.reference);
    } catch (e) {
      toast.error("Could not confirm. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-gold/25 sm:max-w-md" data-testid="booking-dialog">
        {confirmed ? (
          <div className="text-center py-4" data-testid="booking-confirmed">
            <PartyPopper className="w-10 h-10 text-gold mx-auto mb-3" />
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-center">Karibu — you're booked!</DialogTitle>
            </DialogHeader>
            <p className="mt-3 text-sm text-muted-foreground">
              Reference <span className="gold-text font-semibold">{confirmed}</span>
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Your confirmation is on its way by email &amp; WhatsApp. Our team will meet you with the POS on arrival.
            </p>
            <button onClick={onClose} className="mt-5 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-background hover:bg-gold-soft transition-colors" data-testid="booking-done-btn">
              Wonderful
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Confirm your journey</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground -mt-1">{itinerary.title} · <span className="gold-text">${itinerary.total} USD</span></p>
            <div className="space-y-3 mt-2">
              <Input placeholder="Full name" value={form.name} onChange={set("name")} className="bg-white/5 border-gold/20" data-testid="guest-name-input" />
              <Input placeholder="Email" type="email" value={form.email} onChange={set("email")} className="bg-white/5 border-gold/20" data-testid="guest-email-input" />
              <Input placeholder="WhatsApp number (e.g. +2557...)" value={form.phone} onChange={set("phone")} className="bg-white/5 border-gold/20" data-testid="guest-phone-input" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button onClick={payByLink} disabled={loading} className="flex items-center justify-center gap-2 rounded-full bg-gold px-4 py-3 text-sm font-semibold text-background hover:bg-gold-soft transition-colors disabled:opacity-60" data-testid="dialog-pay-link-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay by link
              </button>
              <button onClick={payByPos} disabled={loading} className="flex items-center justify-center gap-2 rounded-full border border-gold/40 px-4 py-3 text-sm text-gold hover:bg-gold/10 transition-colors disabled:opacity-60" data-testid="dialog-pay-pos-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />} Pay at POS
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center mt-1">Card test: 4242 4242 4242 4242 · any future date · any CVC</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
